<template>
    <div>
        <Accordion :multiple="true" :activeIndex="currIndex">
            <AccordionTab v-for="(LO, index) in learningOutcomes" :key="index + 1" :header="'Learning Outcome ' + (index + 1)">
                <!-- <learning-outcome-form :learningOutcome="learningOutcome" /> -->
                <div class="p-fluid formgrid grid">
                    <div class="field col-12 md:col-6">
                        <custom-tree-select v-model="LO.source_id" label="Source" :optionsTree="sourceTree" :errorMessage="formErrors[`learningOutcomes[${index}].sourceId`]" />
                    </div>
                    <div class="field col-12 md:col-6">
                        <custom-tree-select v-model="LO.theme_id" label="Theme" :optionsTree="themeTree" :errorMessage="formErrors[`learningOutcomes[${index}].themeId`]" />
                    </div>
                    <div class="field col-12 md:col-6">
                        <base-dropdown v-model="LO.outcome_type" label="Outcome Type" :options="outcome_types" :errorMessage="formErrors[`learningOutcomes[${index}].outcomeType`]" />
                    </div>
                    <div class="field col-12">
                        <label for="category">Details:</label>
                        <BaseEditor v-model="LO.details" />
                    </div>
                    <div class="field col-12" v-if="LO.task">
                        <label for="category">Existing tasks:</label>
                        <ul class="task-list">
                            <li v-for="task in LO.task" :key="task.id">
                                <!-- <router-link :to="{ name: 'TaskDetails', params: { id: task.id } }"> -->
                                {{ task.title }}
                                <!-- </router-link> -->
                            </li>
                        </ul>
                    </div>

                    <div v-if="editMode" class="field col-12">
                        <Divider />
                        <operational-learning :learningId="LO.learning_id" :operationalLearning="LO.send_to_regional_operational_learning" :OLtype="outcomeLearningType.ROL" label="Send to ROL: " />
                        <operational-learning :learningId="LO.learning_id" :operationalLearning="LO.send_to_national_operational_learning" :OLtype="outcomeLearningType.NOL" label="Send to NOL: " />
                        <operational-learning :learningId="LO.learning_id" :operationalLearning="LO.send_to_joint_operational_learning" :OLtype="outcomeLearningType.JOL" label="Send to JOL: " />
                    </div>

                    <div class="field col-12 grid" v-if="editMode && LO.attachments && LO.attachments.length > 0">
                        <label for="category" class="col-12 mb-2 md:col-6 md:mb-0">Existing Attachments:</label>
                        <ul class="col-12 md:col-4">
                            <li v-for="attachment in LO.attachments" :key="attachment">
                                <a v-if="attachment" href="#" @click="downloadFile(attachment.id, attachment.caption ?? 'Attachment ' + attachment.id)">
                                    {{ attachment.caption ?? 'Attachment ' + attachment.id }}
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div class="field col-12">
                        <CustomFileUpload ref="FileUploading" :index="index" @has-files="hasFiles" @filesUploaded="saveFileIds" />
                    </div>
                </div>
                <div class="col-12 mx-auto">
                    <Button v-if="authStore.hasPermission(UserPermission.DELETE_LEARNING_OUTCOME)" :label="LO?.learning_id ? 'Unlink Learning' : 'Remove Learning'" class="p-button-danger mr-2" @click="confirmRequest({ id: index }, removeLearningTab, 'remove')" :disabled="learningOutcomes.length <= 1" />
                    <Button v-if="authStore.hasPermission(UserPermission.WRITE_LEARNING_OUTCOME)" label="Add New Learning" class="p-button-success mr-2" @click="addLearningTab" />
                    <Button v-if="editMode && authStore.hasPermission(UserPermission.WRITE_TASK)" label="Add Task" class="p-button-warning mr-2 mt-2" @click="addTask(LO.learning_id, LO.outcome_type, LO.source_id, LO.theme_id, LO.details)" :disabled="!LO.learning_id" />
                </div>
            </AccordionTab>
        </Accordion>
    </div>
</template>

<script setup>
import { CustomFileUpload } from '../../../../../modules/custom-inputs';
import OperationalLearning from './OperationalLearning.vue';

import { ref, onMounted, defineExpose, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { authStore, learningOutcomeStore, sourceStore, themeStore, debriefStore, formErrorStore, fileStore, incidentStore } from '../../../../../store/global-stores';
import { useConfirmRemove } from '../../../../../composables/ConfirmComposable';
import { getAllOutcomeTypes } from '../constants/index';
import { outcomeLearningType } from '../enum';
import { UserPermission } from '@/modules/permission/enum';

const { confirmRequest } = useConfirmRemove();
const router = useRouter();
const { sourceTree } = storeToRefs(sourceStore);
const { themeTree } = storeToRefs(themeStore);
const { debrief, editId } = storeToRefs(debriefStore);
const { learningOutcomes } = storeToRefs(learningOutcomeStore);
const { formErrors } = storeToRefs(formErrorStore);
const { incident } = storeToRefs(incidentStore);
const currIndex = ref(null);
const outcome_types = getAllOutcomeTypes();

const emit = defineEmits(['fileUploadingStarted', 'fileUploadingFinished']);

// for files references
const FileUploading = ref(null);
const filesExist = ref([]);
const debriefIdVal = ref(null);

onMounted(async () => {
    sourceStore.fetchAllSources();
    themeStore.fetchAllThemes();
    if (!editMode.value && learningOutcomes.value.length <= 0) {
        addLearningTab();
    }
    openLatestTab();
});

defineExpose({
    async saveLearningOutcomes(debriefId) {
        debriefIdVal.value = debriefId;
        addLearningnOutcomeToDB();
    },
});

/*
Set initially to false.
If there is a file to be uploaded, it's set to true by hasFiles().
Once that the files are completed, it's set to false again by saveFileIds().
 */
const haveToWaitForFiles = ref(false)

//fileUploadingFinished

watch(haveToWaitForFiles, async (newValue) => {
    if (newValue === false ) {
        emit('fileUploadingFinished', haveToWaitForFiles.value);
    }
});

const hasFiles = (event) => {
    filesExist.value[event.refIndex] = event.hasFile;
    haveToWaitForFiles.value = true;
    //console.log('hasFiles', filesExist);

    emit('fileUploadingStarted', haveToWaitForFiles.value);
};

const editMode = computed(() => {
    return editId.value ? true : false;
});

const addLearningnOutcomeToDB = async () => {
    for (let i = 0; i < learningOutcomes.value.length; i++) {
        // check for existing attachments in edit case
        if (editId.value) {
            const existingAttachments = learningOutcomes.value[i].attachments;
            if (existingAttachments && existingAttachments.length > 0) {
                // clean the array before putting it again
                learningOutcomes.value[i].attachments = [];
                existingAttachments.forEach((attachment) => {
                    learningOutcomes.value[i].attachments.push({ id: attachment.id, caption: attachment.caption, description: attachment.description });
                });
            }
        }
        // check if it has files then upload else just save the tab(save on add and update on edit)
        if (filesExist.value[i]) {
            await FileUploading.value[i].uploadFiles();
        } else {
            if (learningOutcomes.value[i]?.learning_id) {
                updateLearningTab(learningOutcomes.value[i]);
            } else {
                saveLearningTab(learningOutcomes.value[i]);
            }
        }
    }
    // Route redirection have been commented out for now because its creating the following issues:-
    // Learning outcomes with attachments are not being saved because, while the attachments save process begins, the operation already redirects back to debrief leading to failure of saving learning outcomes
    // if (!editMode.value) {
    //     //redirect to view debrief list
    //     router.push({ name: 'Debriefs' });
    // }
};
const saveFileIds = async (event) => {
    //console.log('saveFileIds', event.files)
    let attachments = [];
    event.files.forEach((item) => {
        attachments.push({ id: item.id, caption: item.original_name, description: '' });
    });
    learningOutcomes.value[event.refIndex].attachments = [...learningOutcomes.value[event.refIndex].attachments, ...attachments];
    if (learningOutcomes.value[event.refIndex]?.learning_id) {
        updateLearningTab(learningOutcomes.value[event.refIndex]).then(() => {
            haveToWaitForFiles.value = false;
        });
    } else {
        saveLearningTab(learningOutcomes.value[event.refIndex]).then(() => {
            haveToWaitForFiles.value = false;
        });
    }
};
const saveLearningTab = async (obj) => {
    const id = editMode.value ? editId.value : debriefIdVal.value;
    await learningOutcomeStore.createLearningOutcomes(obj, id);
};

const updateLearningTab = async (obj) => {
    await learningOutcomeStore.updateLearningOutcome(obj);
};

const addLearningTab = () => {
    learningOutcomeStore.addLearningOutcomeTab();
    openLatestTab();
};

const removeLearningTab = (index) => {
    if (editMode.value && learningOutcomes.value[index]?.learning_id) {
        // if it exists in db already then it will delete it from db
        unlinkLO(learningOutcomes.value[index]?.learning_id);
    }
    if (index !== -1) {
        learningOutcomes.value.splice(index, 1);

        //Was learning identified value change to 'No' if all learning outcome are removed
        if (learningOutcomes.value.length <= 0) {
            debrief.value.learning = false;
        } else {
            openLatestTab();
        }
    }
};

const openLatestTab = () => {
    let lastIndex = learningOutcomes.value.length - 1;
    currIndex.value = [lastIndex];
};

const downloadFile = async (fileId, fileName) => {
    await fileStore.downloadFile(fileId, fileName);
};

const unlinkLO = async (LOId) => {
    await learningOutcomeStore.deleteLearningOutcome(LOId);
};

const addTask = (learningOutcomeId, outcomeType, source, theme, details) => {
    router.push({
        name: 'TaskForm',
        query: { debriefId: editId.value, learningOutcomeId, outcomeType: outcome_types.find((outcome) => outcome.id == outcomeType)?.name, incidentNo: incident.value.incident_number, source: source, theme: theme, details },
    });
};
</script>
